import { set } from 'lodash-es';
import {
  BooleanField,
  DependencyFunction,
  ModelDependency,
  ModelField,
  ModelGroup,
  ModelItem,
  NumberField,
  StringField
} from '../model-item';
import { FieldType, IModelContext, jaNeinOptions, jaOption } from '../model.types';
import { IModelDataPathPart } from './model-data';
import { ModelItemOptions } from '../model-item-options';
import { requiredValueOptionsValidation } from '../validation/validation.helper';

export interface IDataPathContainer<T> {
  dataPath: string;
  data: T;
}

/**
 * Liefert den Value des Propertyies im IModelContext
 *
 * @param checkValue - Flag ob der ermittelte Wert auch überprüft wird (ob er nicht null oder undefined ist)
 * @param throwError - Flag ob ein Error geworfen wird wenn der Wert null oder undefined ist (nur wenn checkValue)
 */
export function findValueForPropertyInModelContext(
  context: IModelContext,
  objectProperty: string,
  checkValue = false,
  throwError = true
): any {
  return findPropertyInModelContext((value) => value[objectProperty], context, objectProperty, checkValue, throwError);
}

/**
 * Liefert das Object mit dem Key
 * @param checkValue - Flag ob der ermittelte Wert auch überprüft wird (ob er nicht null oder undefined ist)
 * @param throwError - Flag ob ein Error geworfen wird wenn der Wert null oder undefined ist (nur wenn checkValue)
 *
 */
export function findObjectForPropertyInModelContext(
  context: IModelContext,
  objectProperty: string,
  checkValue = false,
  throwError = true
): any {
  return findPropertyInModelContext((value) => value, context, objectProperty, checkValue, throwError);
}

/**
 * Liefert das Object mit dem Key
 */
function findPropertyInModelContext(
  dataExtractor: (value: object) => any,
  context: IModelContext,
  objectProperty: string,
  checkValue = false,
  throwError = true
): any {
  let value: any;

  for (let i = context.dataStack.length - 1; i >= 0; i--) {
    const stackValue: any = context.dataStack[i];
    if (stackValue && typeof stackValue === 'object' && stackValue[objectProperty]) {
      value = dataExtractor(stackValue);
      break;
    }
  }
  if (checkValue) {
    checkContextProperty(value, context, objectProperty, throwError);
  }

  return value;
}

export function checkContextProperty(value: any, modelContext: IModelContext, propertyName: string, throwError = true) {
  if (typeof value === 'undefined' || value === null) {
    console.trace(`Property ${propertyName} - context:`, modelContext);
    if (throwError) {
      throw Error(`Wert für Property ${propertyName} ist nicht definiert`);
    }
  }
}

export function isFrageTrue(propertyName: string): DependencyFunction {
  return (dependency: ModelDependency, modelContext: IModelContext): boolean => {
    const result = !!findValueForPropertyInModelContext(modelContext, propertyName);
    // console.log('isFrageTrue', propertyName, result);
    return result;
  };
}

/**
 * Liefert Funktion die true liefert wenn eine der übergebenen DependencyFunctions true liefert sonst false
 *
 */
export function orDependencies(...dependencyFunctions: DependencyFunction[]): DependencyFunction {
  return (dependency: ModelDependency, modelContext: IModelContext): boolean => {
    for (const dependencyFunction of dependencyFunctions) {
      if (dependencyFunction(dependency, modelContext)) {
        return true;
      }
    }
    return false;
  };
}

export function createBooleanWithDependentTextFieldCombo(
  booleanId: string,
  booleanDesc: string,
  textDesc: string,
  func?: DependencyFunction,
  dependentValue = true
): ModelField[] {
  const textField: StringField = new StringField(`${booleanId}Detail`, textDesc).withRemoveDataWhenNotRelevant(true);
  const booleanField: BooleanField = createBooleanWithDependentItem(
    booleanId,
    booleanDesc,
    textField,
    func,
    dependentValue
  );

  return [booleanField, textField];
}
export function createJaNeinOptionWithDependentTextFieldCombo(
  optionJaNeinId: string,
  optionJaNeinDesc: string,
  textDescs: string[],
  func?: DependencyFunction,
  maxLength?: number,
  dependentValue = true
): ModelField[] {
  const textFields: StringField[] = [];
  textDescs.forEach((textDesc, index) => {
    let textField: StringField = new StringField(
      `${optionJaNeinId}_${index}_Detail`,
      textDesc
    ).withRemoveDataWhenNotRelevant(true);
    if (maxLength) {
      textField = textField.withMaxlength(maxLength);
    }
    textFields.push(textField);
  });
  const numberField: NumberField = createJaNeinWithDependentItem(
    optionJaNeinId,
    optionJaNeinDesc,
    textFields,
    func,
    dependentValue
  );
  return [numberField, ...textFields];
}

export function createBooleanWithDependentNumberFieldCombo(
  booleanId: string,
  booleanDesc: string,
  textDesc: string,
  func?: DependencyFunction,
  dependentValue = true
): ModelField[] {
  const numberField: NumberField = new NumberField(`${booleanId}Detail`, textDesc).withRemoveDataWhenNotRelevant(true);
  const booleanField: BooleanField = createBooleanWithDependentItem(
    booleanId,
    booleanDesc,
    numberField,
    func,
    dependentValue
  );

  return [booleanField, numberField];
}

export function createBooleanWithDependentItem(
  booleanId: string,
  booleanDesc: string,
  modelItem: ModelItem,
  func?: DependencyFunction,
  dependentValue = true
): BooleanField {
  const booleanField: BooleanField = new BooleanField(booleanId, booleanDesc);

  if (func) {
    booleanField.withFunctionDependency(func, false); // --bnd-- durch: ,false sind sämtliche Booleanfields nicht mehr required und werden bei nichtvorhandensein als false gewertet.
  }

  const dependencyFunction: DependencyFunction = createDependencyFunctionOnBooleanField(
    booleanField,
    modelItem,
    dependentValue
  );

  modelItem.withFunctionDependency(dependencyFunction);

  return booleanField;
}
export function createJaNeinWithDependentItem(
  jaNeinId: string,
  jaNeinDesc: string,
  modelItems: ModelItem[],
  func?: DependencyFunction,
  dependentValue = true
): NumberField {
  const numberField: NumberField = new NumberField(jaNeinId, jaNeinDesc).withOptions(
    new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(jaNeinOptions)
  );

  if (func) {
    numberField.withFunctionDependency(func, false); // --bnd-- durch: ,false sind sämtliche Booleanfields nicht mehr required und werden bei nichtvorhandensein als false gewertet.
  }

  let item: ModelItem;
  for (item of modelItems) {
    const dependencyFunction: DependencyFunction = createDependencyFunctionOnJaNeinField(
      numberField,
      item,
      dependentValue
    );
    item.withFunctionDependency(dependencyFunction);
  }

  return numberField;
}

export function createDependencyFunctionOnJaNeinField(
  numberField: NumberField,
  dependentModelItem?: ModelItem,
  dependentValue = true
): DependencyFunction {
  const dependencyFunction: DependencyFunction = (
    dependency: ModelDependency,
    modelContext: IModelContext
  ): boolean => {
    let required = true;
    const hierarchieLevel: number = dependentModelItem && dependentModelItem instanceof ModelGroup ? 2 : 1;

    if (modelContext.dataStack.length > hierarchieLevel - 1) {
      const data: object = modelContext.dataStack[modelContext.dataStack.length - hierarchieLevel];
      const booleanValue = !!(data && data[numberField.id] && data[numberField.id] === jaOption.value);
      required = booleanValue === dependentValue;
    }
    return required;
  };
  return dependencyFunction;
}
export function createDependencyFunctionOnBooleanField(
  booleanField: BooleanField,
  dependentModelItem?: ModelItem,
  dependentValue = true
): DependencyFunction {
  const dependencyFunction: DependencyFunction = (
    dependency: ModelDependency,
    modelContext: IModelContext
  ): boolean => {
    let required = true;
    const hierarchieLevel: number = dependentModelItem && dependentModelItem instanceof ModelGroup ? 2 : 1;

    if (modelContext.dataStack.length > hierarchieLevel - 1) {
      const data: object = modelContext.dataStack[modelContext.dataStack.length - hierarchieLevel];
      const booleanValue = !!(data && data[booleanField.id]);
      required = booleanValue === dependentValue;
    }
    return required;
  };
  return dependencyFunction;
}

export function createDependenciesOnBooleanField(
  booleanField: BooleanField,
  items: ModelItem[],
  isRequired = false,
  dependentValue = true
): ModelItem[] {
  let item: ModelItem;
  for (item of items) {
    const dependencyFunction: DependencyFunction = createDependencyFunctionOnBooleanField(
      booleanField,
      item,
      dependentValue
    );
    item.withFunctionDependency(dependencyFunction, isRequired);
  }

  return items;
}

export function createDependencies(
  items: ModelItem[],
  dependencyFunction: DependencyFunction,
  isRequired?: boolean
): ModelItem[] {
  let item: ModelItem;
  for (item of items) {
    item.withFunctionDependency(dependencyFunction, isRequired, item);
  }

  return items;
}

export function computeDataAccessPath(
  modelItem: ModelItem,
  modelContext: IModelContext,
  suppressRootNode = false,
  fieldParent = ''
): string {
  let path = null;

  let part: IModelDataPathPart;
  let lastPart: string; // da für Arrays 2 Einträge vorhanden sind (1 für das Array und 1 für die konkrete Ausprägung mit Index)

  let index = -1;
  for (part of [...modelContext.parts].reverse()) {
    ++index;
    if (suppressRootNode && index === modelContext.parts.length - 1) {
      break;
    }

    if (part.pathPart && part.pathPart.length > 0 && part.pathPart !== lastPart) {
      lastPart = part.pathPart;
      const arrayNotation = part.index >= 0 ? `[${part.index}]` : '';
      path = `${part.pathPart}${arrayNotation}${path ? '.' + path : ''}`;
    }
  }
  if (modelItem instanceof ModelField) {
    const id = modelItem.getDataId();
    if (modelContext.parts.length === 0 || modelContext.parts[modelContext.parts.length - 1].pathPart !== id) {
      if (fieldParent === '') {
        path = `${path ? path + '.' : ''}${id}`;
      } else {
        path = `${path ? path + '.' : ''}${fieldParent}.${id}`;
      }
    }
  }
  return path ? path : '';
}

export function createDataObject<T>(containers: IDataPathContainer<T>[]): object {
  const result = {};
  let container: IDataPathContainer<T>;
  for (container of containers) {
    set(result, container.dataPath, container.data);
  }
  return result;
}

export function getFieldsOfType(items: ModelItem[], fieldType: FieldType): ModelField[] {
  return items.filter((modelItem) => modelItem instanceof ModelField && fieldType === modelItem.type) as ModelField[];
}
